import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Radio } from 'components/UI/V3/Forms/Radio/Radio';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import CriterionLookupField from './CriterionLookupField';
import { FIELD_OPERATOR_LABELS, effectiveBooleanValue } from './criterionLabels';
import { type Criterion, type CriterionValue, type OperatorType } from '@spike/automation-model';
import styles from './CriterionAccordion.module.scss';

interface Props {
    definition: Criterion;
    value: CriterionValue | null;
    operatorLabels?: Record<string, string>;
    onChange: (value: CriterionValue | null) => void;
}

const uid = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;

const OPERATOR_FALLBACKS: Record<string, string> = {
    is: 'is',
    is_not: 'is not',
    in: 'is one of',
    not_in: 'is not one of',
    gt: 'Greater than',
    lt: 'Less than',
    eq: 'Equal to',
    days_ago_gt: 'More than',
    days_ago_lt: 'Less than',
    days_ago_eq: 'Exactly'
};

const CriterionAccordion: FunctionComponent<Props> = ({ definition, value, operatorLabels, onChange }) => {
    const [open, setOpen] = useState(false);
    const isActive = value !== null;

    const operators = definition.operators?.length ? definition.operators : [definition.defaultOperator];
    const labelFor = (op: OperatorType) =>
        FIELD_OPERATOR_LABELS[definition.field]?.[op] ?? operatorLabels?.[op] ?? OPERATOR_FALLBACKS[op] ?? op;

    const negation = operators.find(op => op === 'is_not' || op === 'not_in');
    const affirmation = operators.find(op => op === 'is' || op === 'in');
    const currentOperator = (isActive ? value!.operator : definition.defaultOperator) as OperatorType;
    const isNegated = negation !== undefined && currentOperator === negation;

    // Every field renders the same way: operator on top, values underneath.
    // The three field types used to each place it differently — boolean put it
    // last, so "is / is not" appeared below the options it governed.
    const renderOperatorRow = () => {
        // With two options, Yes/No already expresses the negation.
        if (definition.fieldType === 'boolean') return null;

        const choices = negation && affirmation ? [affirmation, negation] : operators.length > 1 ? operators : [];

        if (choices.length === 0) return null;

        return (
            <div className={styles.operatorRow}>
                {choices.map(op => (
                    <Radio
                        key={op}
                        checked={currentOperator === op}
                        onChange={() => update({ operator: op })}
                        inlineLabel={labelFor(op)}
                    />
                ))}
            </div>
        );
    };

    const ensureValue = (): CriterionValue =>
        value ?? {
            id: uid(),
            field: definition.field,
            operator: definition.defaultOperator,
            value: definition.defaultValue
        };

    const update = (patch: Partial<CriterionValue>) => onChange({ ...ensureValue(), ...patch });

    // ── Boolean ────────────────────────────────────────────────────────────────
    const renderBoolean = () => {
        const selected = isActive ? effectiveBooleanValue(value!, definition) : undefined;

        return (definition.options ?? []).map(opt => (
            <Radio
                key={opt.value}
                checked={selected === opt.value}
                onChange={() => update({ operator: 'is', value: opt.value })}
                inlineLabel={opt.label}
            />
        ));
    };

    // ── Multi-enum ─────────────────────────────────────────────────────────────
    const renderMultiEnum = () => {
        const selected = isActive ? (value!.value as string[]) ?? [] : [];
        const operator = isNegated && negation ? negation : affirmation ?? definition.defaultOperator;

        const setSelection = (next: string[]) =>
            onChange(next.length > 0 ? { ...ensureValue(), operator, value: next } : null);

        if (definition.lookup) {
            return (
                <CriterionLookupField
                    lookup={definition.lookup}
                    selected={selected}
                    onChange={setSelection}
                />
            );
        }

        const toggle = (v: string) =>
            setSelection(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

        if ((definition.options ?? []).length === 0) {
            return (
                <p className={styles.emptyOptions}>
                    Nothing configured for this yet. Set it up in your business settings first.
                </p>
            );
        }

        return (definition.options ?? []).map(opt => (
            <Checkbox
                key={opt.value}
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                label={opt.label}
            />
        ));
    };

    // ── Numeric-ish (numeric and date_relative share the shape) ───────────────
    const renderComparison = (suffix?: string) => {
        const currentVal = isActive ? (value!.value as number) : (definition.defaultValue as number);

        return (
            <div className={styles.inputRow}>
                {!suffix && definition.unit && <span className={styles.unit}>{definition.unit}</span>}
                <input
                    type="number"
                    className={styles.numberInput}
                    min={0}
                    value={currentVal ?? 0}
                    onChange={e => update({ value: Number(e.target.value) })}
                    onClick={e => e.stopPropagation()}
                />
                {suffix && (
                    <span className={styles.unit}>
                        {definition.unit} {suffix}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className={styles.row}>
            <div
                className={styles.header}
                onClick={() => setOpen(o => !o)}
            >
                <div className={styles.headerLeft}>
                    {isActive && <div className={styles.activeDot} />}
                    <span className={styles.label}>{definition.label}</span>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={clsx(styles.chevron, { [styles.open]: open })}
                />
            </div>

            {open && (
                <div className={styles.body}>
                    {renderOperatorRow()}
                    <div className={styles.values}>
                        {definition.fieldType === 'boolean' && renderBoolean()}
                        {definition.fieldType === 'multi_enum' && renderMultiEnum()}
                        {definition.fieldType === 'date_relative' && renderComparison('ago')}
                        {definition.fieldType === 'numeric' && renderComparison()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CriterionAccordion;
